import {
  AdjacentPostData,
  PostData,
} from '../@types/postData'

import {
  ModalElements,
  ModalElementsError
} from '../@types/elements'

export const getRequiredElement = <T extends Element>(parent: Document | Element, selector: string): T => {
  const el = parent.querySelector<T>(selector)
  if (!el) {
    throw new Error(`Element ${selector} is required`)
  }
  return el
}

export const getRequiredElements = <T extends Element>(parent: Document | Element, selector: string): NodeListOf<T> => {
  const el = parent.querySelectorAll<T>(selector)
  if (!el) {
    throw new Error(`Element ${selector} is required`)
  }
  return el
}

export const getReaderModalElements = (requestID: string): ModalElements | ModalElementsError => {
  if (!requestID) {
    return {
      message: 'requestID is required'
    }
  }

  const readerModal = document.querySelector<HTMLElement>(`#uctoday-reader-modal-${requestID}`)
  if (!readerModal) {
    return {
      message: 'readerModal is required'
    }
  }
  const modalFeaturedImage = getRequiredElement<HTMLImageElement>(readerModal, '.featured-image')
  const modalTitle = getRequiredElement<HTMLElement>(readerModal, '.title')
  const modalByline = getRequiredElement<HTMLElement>(readerModal, '.byline')
  const modalContent = getRequiredElement<HTMLElement>(readerModal, '.content')
  const modalPrevious = getRequiredElement<HTMLButtonElement>(readerModal, '.previous-post button')
  const modalNext = getRequiredElement<HTMLButtonElement>(readerModal, '.next-post button')
  const modalCloseButton = getRequiredElement<HTMLButtonElement>(readerModal, '.close-modal')
  const loadingWrapper = getRequiredElement<HTMLElement>(readerModal, '.loading-wrapper')
  const postWrapper = getRequiredElement<HTMLElement>(readerModal, '.post-wrapper')
  const socialShareWrapper = getRequiredElement<HTMLElement>(readerModal, '.social-share-wrapper')
  return {
    readerModal,
    modalFeaturedImage,
    modalTitle,
    modalByline,
    modalContent,
    modalPrevious,
    modalNext,
    modalCloseButton,
    loadingWrapper,
    postWrapper,
    socialShareWrapper,
  }
}

export const doModalMarkup = (data: PostData, modalElements: ModalElements) => {
  const author = data._embedded.author ? data._embedded.author[0].name : ''
  const publishedDate = new Date(data.date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })

  const byLine = `${publishedDate} | ${author}`
  modalElements.modalTitle.innerHTML = data.title.rendered
  modalElements.modalByline.innerHTML = byLine

  if (data._embedded['wp:featuredmedia'] && data._embedded['wp:featuredmedia'][0]['source_url']) {
    modalElements.modalFeaturedImage.src = data._embedded['wp:featuredmedia'][0]['source_url']
  } else {
    const defaultSrc = modalElements.modalFeaturedImage.getAttribute('data-default-src') as string
    modalElements.modalFeaturedImage.src = defaultSrc
  }

  if (data._embedded['wp:featuredmedia'] && data._embedded['wp:featuredmedia'][0]['alt_text']) {
    modalElements.modalFeaturedImage.alt = data._embedded['wp:featuredmedia'][0]['alt_text']
  } else {
    const defaultAlt = modalElements.modalFeaturedImage.getAttribute('data-default-alt') as string
    modalElements.modalFeaturedImage.alt = defaultAlt
  }
  
  modalElements.modalContent.innerHTML = data.meta.video_data ? 
    `<iframe width="100%" height="500px" src="${data.meta.video_data.embed_url}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe><br />${data.content.rendered}` :
    data.content.rendered
  doSocialLinks(data, modalElements)
}

export const doAdjacentPostMarkup = (withData: HTMLElement, modalPrevious: HTMLButtonElement, modalNext: HTMLButtonElement) => {
  const adjacentPostData = JSON.parse(withData.dataset.adjacentPosts as string) as AdjacentPostData
  const previousPostLink = adjacentPostData?.['previous-post'].href
  const nextPostLink = adjacentPostData?.['next-post'].href

  if (previousPostLink) {
    modalPrevious.dataset.selfHref = previousPostLink
    modalPrevious.disabled = false
  } else {
    modalPrevious.disabled = true
    modalPrevious.dataset.selfHref = ''
  }

  if (nextPostLink) {
    modalNext.dataset.selfHref = nextPostLink
    modalNext.disabled = false
  } else {
    modalNext.disabled = true
    modalNext.dataset.selfHref = ''
  }
}

export const doImageMarkup = (modalContent: HTMLElement) => {
  const withImages = getRequiredElements<HTMLImageElement>(modalContent, 'img')

  if (!withImages.length) {
    return
  }

  withImages.forEach(img => {
    const sourceData = img.getAttribute('data-src')
    if (!sourceData) {
      return
    }
    img.dataset.processed = 'true'
    img.src = sourceData
  }, { once: true })
}

const doSocialLinks = ({ title, link }: PostData, modalElements: ModalElements) => {
  doEmailLink(title.rendered, link, modalElements)
  doTwitterLink(title.rendered, link, modalElements)
  doLinkedInLink(title.rendered, link, modalElements)
  doFacebookLink(link, modalElements)
  doRedditLink(title.rendered, link, modalElements)
}

const doEmailLink = (title: string, link: string, modalElements: ModalElements) => {
  const emailLink = getRequiredElement<HTMLAnchorElement>(modalElements.socialShareWrapper, '#email-share-link')
  emailLink.href = `mailto:?subject=${title}&body=Check out this UConn Today article ${link}`
}

const doTwitterLink = (title: string, link: string, modalElements: ModalElements) => {
  const twitterLink = getRequiredElement<HTMLAnchorElement>(modalElements.socialShareWrapper, '#twitter-share-link')
  twitterLink.href = `https://twitter.com/intent/tweet?text=${title}&url=${encodeURIComponent(link)}`
}

const doLinkedInLink = (title: string, link: string, modalElements: ModalElements) => {
  const linkedInLink = getRequiredElement<HTMLAnchorElement>(modalElements.socialShareWrapper, '#linkedin-share-link')
  linkedInLink.href = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`
}

const doFacebookLink = (link: string, modalElements: ModalElements) => {
  const facebookLink = getRequiredElement<HTMLAnchorElement>(modalElements.socialShareWrapper, '#facebook-share-link')
  facebookLink.href = `https://www.facebook.com/dialog/share?app_id=1689478697951363&display=popup&href=${encodeURIComponent(link)}&redirect_uri=${encodeURIComponent(link)}`
}

const doRedditLink = (title: string, link: string, modalElements: ModalElements) => {
  const redditLink = getRequiredElement<HTMLAnchorElement>(modalElements.socialShareWrapper, '#reddit-share-link')
  redditLink.href = `https://www.reddit.com/submit?url=${encodeURIComponent(link)}&title=${encodeURIComponent(title)}`
}